import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { AdoptionRateDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { MeterChart } from "@/components/Charts/MeterChart";
import { dataColors } from "@/components/Charts/theme";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const FALLBACK_ADOPTION_STATS: AdoptionRateDto = {
  adoptionRatePercentage: 0,
  reachRatePercentage: 0,
  totalAddresses: 0,
  totalAddressesInvited: 0,
  invitedAddressesActivated: 0,
  invitedAddressesNotActivated: 0,
  unknownStatusAddresses: 0,
  totalResidents: 0,
};

export function AdoptionWidget(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  const analyticsQueries = useAnalyticsQueries();

  const { data: adoption, isPending, isError, error, refetch, isRefetching } = useQuery(analyticsQueries.adoption());

  const handleReload = () => {
    void refetch();
  };

  const isLoading = isPending || isRefetching;
  const primaryData = adoption || FALLBACK_ADOPTION_STATS;
  const missingAddresses = primaryData.totalAddresses - primaryData.totalAddressesInvited;
  const isDataAvailable = primaryData.totalAddressesInvited > 0 && primaryData.totalAddresses > 0;
  const isActionRequired = !isLoading && !isError && isDataAvailable && missingAddresses > 0;

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.reach-adoption.title")}
      href={isDataAvailable ? routes.analytics.adoptionDetails({ slug }) : undefined}
      isLoading={isLoading}
      isError={isError}
      isDataAvailable={isDataAvailable}
      error={error}
      onReload={handleReload}
    >
      <AnalyticsWidgetCard.Body>
        <MeterChart
          primaryValue={primaryData.reachRatePercentage}
          secondaryValue={primaryData.adoptionRatePercentage}
          ariaLabel="Reach & adoption rate chart"
        />
        <ChartLegend
          vertical
          items={[
            {
              label: (
                <Trans
                  i18nKey="component.analytics-widget.reach-adoption.chart.legend.primary"
                  components={{
                    b: <strong />,
                  }}
                  values={{
                    reachCount: primaryData.invitedAddressesNotActivated + primaryData.invitedAddressesActivated,
                    count: primaryData.totalAddresses,
                  }}
                />
              ),
              color: dataColors.success,
            },
            {
              label: (
                <Trans
                  i18nKey="component.analytics-widget.reach-adoption.chart.legend.secondary"
                  components={{
                    b: <strong />,
                  }}
                  values={{
                    count: primaryData.invitedAddressesActivated,
                  }}
                />
              ),
              color: dataColors.primary,
            },
          ]}
        />
      </AnalyticsWidgetCard.Body>
      <AnalyticsWidgetCard.Footer>
        {isActionRequired && (
          <Stack gap="lg" align="center">
            <Button type="link" href={routes.addresses.list({ slug })}>
              {t("component.analytics-widget.reach-adoption.action-btn")}
            </Button>

            <section className="text-left">
              <h4 className="text-heading-4">
                {t("component.analytics-widget.reach-adoption.action-title", {
                  count: missingAddresses,
                })}
              </h4>
              <p>
                {t("component.analytics-widget.reach-adoption.action-description", {
                  count: missingAddresses,
                })}
              </p>
            </section>
          </Stack>
        )}
      </AnalyticsWidgetCard.Footer>
    </AnalyticsWidgetCard.Root>
  );
}
